import React from 'react';
import { token } from '@atlaskit/tokens';
import { add } from './percentageOfLine.tsx';
import { type Point, toPoint, TransitionIcons } from './toPortInfo.tsx';

const ICON_PADDING = 18;

interface IconProps {
	anchor: Point;
	inverse?: boolean;
}

const BoltIcon = ({ anchor, inverse = false }: IconProps) => (
	<path
		data-testid="accessible-workflow-diagram.bolt-icon"
		fill={inverse ? token('color.icon.inverse') : token('color.icon')}
		stroke="none"
		d="M10.339854,4L15.1874898,4C15.4636322,4,15.6874898,4.22385763,15.6874898,4.5C15.6874898,4.57818367,15.6691551,4.65527785,15.6339583,4.72509094L13.5848909,8.78942131C13.4605766,9.03599902,13.5596907,9.3366664,13.8062684,9.46098069C13.8760815,9.49617756,13.9531756,9.51451225,14.0313593,9.51451225L17.65813,9.51451225C17.9342724,9.51451225,18.15813,9.73836987,18.15813,10.0145122C18.15813,10.1589731,18.0956491,10.2963739,17.9867788,10.3913278L7.71485013,19.3502415C7.50674068,19.5317492,7.19089352,19.5101843,7.00938577,19.3020748C6.88345852,19.1576918,6.85110443,18.9540478,6.92608257,18.7777457L8.97708607,13.9550573C9.08515731,13.7009408,8.9667642,13.4073296,8.71264766,13.2992583C8.65076634,13.2729413,8.58421231,13.259377,8.51696736,13.259377L6.12950524,13.259377C5.85336286,13.259377,5.62950524,13.0355194,5.62950524,12.759377C5.62950524,12.6804396,5.64819503,12.6026237,5.68404531,12.5322967L9.8943941,4.27291972C9.97976952,4.10544006,10.1518689,4,10.339854,4Z"
		transform={`matrix(0.7982,0,0,0.8617,${anchor.x},${anchor.y})`}
	/>
);

const CheckIcon = ({ anchor }: IconProps) => (
	<path
		data-testid="accessible-workflow-diagram.check-icon"
		fill={token('color.icon.success')}
		stroke="none"
		d="M18,0 C27.9411255,0 36,8.0588745 36,18 C36,27.9411255 27.9411255,36 18,36 C8.0588745,36 0,27.9411255 0,18 C0,8.0588745 8.0588745,0 18,0 Z M22.5857864,12.5857864 L16,19.1715729 L13.4142136,16.5857864 C12.633165,15.8047379 11.366835,15.8047379 10.5857864,16.5857864 C9.8047379,17.366835 9.8047379,18.633165 10.5857864,19.4142136 L14.5857864,23.4142136 C15.366835,24.1952621 16.633165,24.1952621 17.4142136,23.4142136 L25.4142136,15.4142136 C26.1952621,14.633165 26.1952621,13.366835 25.4142136,12.5857864 C24.633165,11.8047379 23.366835,11.8047379 22.5857864,12.5857864 Z"
		transform={`matrix(0.4,0,0,0.4,${anchor.x + 1.5},${anchor.y + 3})`}
	/>
);

const CheckInactiveIcon = ({ anchor }: IconProps) => (
	<path
		data-testid="accessible-workflow-diagram.check-inactive-icon"
		fill={token('color.text.subtlest')}
		stroke="none"
		d="M18,0 C27.9411255,0 36,8.0588745 36,18 C36,27.9411255 27.9411255,36 18,36 C8.0588745,36 0,27.9411255 0,18 C0,8.0588745 8.0588745,0 18,0 Z M22.5857864,12.5857864 L16,19.1715729 L13.4142136,16.5857864 C12.633165,15.8047379 11.366835,15.8047379 10.5857864,16.5857864 C9.8047379,17.366835 9.8047379,18.633165 10.5857864,19.4142136 L14.5857864,23.4142136 C15.366835,24.1952621 16.633165,24.1952621 17.4142136,23.4142136 L25.4142136,15.4142136 C26.1952621,14.633165 26.1952621,13.366835 25.4142136,12.5857864 C24.633165,11.8047379 23.366835,11.8047379 22.5857864,12.5857864 Z"
		transform={`matrix(0.4,0,0,0.4,${anchor.x + 1.5},${anchor.y + 3})`}
	/>
);

const CrossIcon = ({ anchor }: IconProps) => (
	<path
		data-testid="accessible-workflow-diagram.cross-icon"
		fill={token('color.icon.danger')}
		stroke="none"
		d="M18,0 C27.9411255,0 36,8.0588745 36,18 C36,27.9411255 27.9411255,36 18,36 C8.0588745,36 0,27.9411255 0,18 C0,8.0588745 8.0588745,0 18,0 Z M12.1745714,12.1742923 C11.3918678,12.9536824 11.3891818,14.2200095 12.1685719,15.0027131 L15.183,18.03 L12.2338602,20.9972333 C11.4551613,21.7806245 11.458965,23.0469487 12.2423561,23.8256477 C13.0257473,24.6043467 14.2920715,24.6005429 15.0707705,23.8171518 L18.005,20.864 L20.8848942,23.7560915 C21.6642843,24.538795 22.9306114,24.5414811 23.7133149,23.762091 C24.4960185,22.9827009 24.4987046,21.7163738 23.7193145,20.9336703 L20.825,18.027 L23.7899828,15.0454007 C24.5686818,14.2620096 24.564878,12.9956853 23.7814869,12.2169863 C22.9980957,11.4382874 21.7317715,11.4420911 20.9530725,12.2254822 L18.003,15.193 L15.0029922,12.1802919 C14.2236021,11.3975883 12.957275,11.3949022 12.1745714,12.1742923 Z"
		transform={`matrix(0.4,0,0,0.4,${anchor.x + 1.5},${anchor.y + 3})`}
	/>
);

const CrossInactiveIcon = ({ anchor }: IconProps) => (
	<path
		data-testid="accessible-workflow-diagram.cross-inactive-icon"
		fill={token('color.text.subtlest')}
		stroke="none"
		d="M18,0 C27.9411255,0 36,8.0588745 36,18 C36,27.9411255 27.9411255,36 18,36 C8.0588745,36 0,27.9411255 0,18 C0,8.0588745 8.0588745,0 18,0 Z M12.1745714,12.1742923 C11.3918678,12.9536824 11.3891818,14.2200095 12.1685719,15.0027131 L15.183,18.03 L12.2338602,20.9972333 C11.4551613,21.7806245 11.458965,23.0469487 12.2423561,23.8256477 C13.0257473,24.6043467 14.2920715,24.6005429 15.0707705,23.8171518 L18.005,20.864 L20.8848942,23.7560915 C21.6642843,24.538795 22.9306114,24.5414811 23.7133149,23.762091 C24.4960185,22.9827009 24.4987046,21.7163738 23.7193145,20.9336703 L20.825,18.027 L23.7899828,15.0454007 C24.5686818,14.2620096 24.564878,12.9956853 23.7814869,12.2169863 C22.9980957,11.4382874 21.7317715,11.4420911 20.9530725,12.2254822 L18.003,15.193 L15.0029922,12.1802919 C14.2236021,11.3975883 12.957275,11.3949022 12.1745714,12.1742923 Z"
		transform={`matrix(0.4,0,0,0.4,${anchor.x + 1.5},${anchor.y + 3})`}
	/>
);

const ErrorIcon = ({ anchor, inverse = false }: IconProps) => (
	<path
		data-testid="accessible-workflow-diagram.error-icon"
		fill={inverse ? token('color.icon.inverse') : token('color.icon')}
		stroke="none"
		d="M7.28711356,1.29771564 L7.4143185,1.41458101 L10.5856815,4.58607097 C11.3279955,5.32841471 11.3648683,6.50189375 10.7025422,7.28746336 L10.5856815,7.41465944 L7.4143185,10.5857867 C6.67200448,11.3280455 5.49850741,11.3649156 4.71288644,10.7026387 L4.5856815,10.5857867 L1.4143185,7.41465944 C0.67200448,6.67240061 0.63513165,5.49892578 1.29745781,4.71328041 L1.4143185,4.58607097 L4.5856815,1.41458101 C5.32799552,0.67223727 6.50149259,0.63536297 7.28711356,1.29771564 Z M6,8 L5.90152767,8.0072327 C5.58016529,8.054876 5.33333333,8.3321212 5.33333333,8.6666667 L5.33333333,8.6666667 L5.34056599,8.765139 C5.38820937,9.0865014 5.66545455,9.3333333 6,9.3333333 L6,9.3333333 L6.09847233,9.3261007 C6.41983471,9.2784573 6.66666667,9.0012121 6.66666667,8.6666667 L6.66666667,8.6666667 L6.65943401,8.5681943 C6.61179063,8.246832 6.33454545,8 6,8 L6,8 Z M6,2.66666667 L5.90152767,2.67389932 C5.61230153,2.71677836 5.38344503,2.94563486 5.34056599,3.23486101 L5.33333333,3.33333333 L5.33333333,6.66666667 C5.33333333,7.00121212 5.58016529,7.2784573 5.90152767,7.32610068 L6,7.33333333 L6.09847233,7.32610068 C6.38769847,7.28322164 6.61655497,7.05436514 6.65943401,6.76513899 L6.66666667,6.66666667 L6.66666667,3.33333333 C6.66666667,2.96533333 6.368,2.66666667 6,2.66666667 Z"
		transform={`matrix(1.3,0,0,1.3,${anchor.x + 1.5},${anchor.y + 2})`}
	/>
);

const SharedTransitionIcon = ({ anchor }: IconProps) => (
	<path
		data-testid="accessible-workflow-diagram.shared-transition-icon"
		fillRule="evenodd"
		clipRule="evenodd"
		d="M5.48039 0.214666L3.52772 2.18467C3.40318 2.3106 3.33333 2.48056 3.33333 2.65767C3.33333 2.83478 3.40318 3.00474 3.52772 3.13067C3.58888 3.19277 3.66179 3.24209 3.74219 3.27575C3.82259 3.30941 3.90889 3.32675 3.99605 3.32675C4.08322 3.32675 4.16951 3.30941 4.24992 3.27575C4.33032 3.24209 4.40322 3.19277 4.46439 3.13067L5.33333 2.25454V4.66667H2C1.63181 4.66667 1.33333 4.96514 1.33333 5.33333V8.1138C0.556539 8.38836 0 9.12919 0 10C0 11.1046 0.895431 12 2 12C3.10457 12 4 11.1046 4 10C4 9.12919 3.44346 8.38836 2.66667 8.1138V6H5.33333L6 6L6.66667 6L9.33333 6V8.1138C8.55654 8.38836 8 9.12919 8 10C8 11.1046 8.89543 12 10 12C11.1046 12 12 11.1046 12 10C12 9.12919 11.4435 8.38836 10.6667 8.1138V5.33333C10.6667 4.96514 10.3682 4.66667 10 4.66667H6.66667V2.26209L7.53439 3.13733C7.59571 3.1994 7.66874 3.24867 7.74925 3.2823C7.82976 3.31593 7.91614 3.33325 8.00339 3.33325C8.09064 3.33325 8.17702 3.31593 8.25752 3.2823C8.33803 3.24867 8.41106 3.1994 8.47239 3.13733C8.59676 3.01132 8.6665 2.84139 8.6665 2.66433C8.6665 2.48728 8.59676 2.31735 8.47239 2.19133L6.51239 0.214666C6.37456 0.077525 6.18815 0.000372232 5.99372 0C5.80772 0 5.62239 0.071333 5.48039 0.214666ZM1.33333 10C1.33333 9.63181 1.63181 9.33333 2 9.33333C2.36819 9.33333 2.66667 9.63181 2.66667 10C2.66667 10.3682 2.36819 10.6667 2 10.6667C1.63181 10.6667 1.33333 10.3682 1.33333 10ZM9.33333 10C9.33333 9.63181 9.63181 9.33333 10 9.33333C10.3682 9.33333 10.6667 9.63181 10.6667 10C10.6667 10.3682 10.3682 10.6667 10 10.6667C9.63181 10.6667 9.33333 10.3682 9.33333 10Z"
		fill={token('color.icon')}
		transform={`matrix(1.1,0,0,1.1,${anchor.x + 1.2},${anchor.y + 3})`}
	/>
);

// Subject to change based on design review, see https://jplat.atlassian.net/browse/CRCS-2854
const TriggerAgentIcon = ({ anchor, inverse = false }: IconProps) => (
	<path
		data-testid="accessible-workflow-diagram.trigger-agent-icon"
		clipRule="evenodd"
		fillRule="evenodd"
		stroke="none"
		d="M6.971.493a2 2 0 0 1 2.058 0l5 3A2 2 0 0 1 15 5.208v5.584a2 2 0 0 1-.971 1.715l-5 3a2 2 0 0 1-2.058 0l-5-3A2 2 0 0 1 1 10.792V5.208a2 2 0 0 1 .971-1.715zm1.286 1.286a.5.5 0 0 0-.514 0l-5 3a.5.5 0 0 0-.243.429v5.584a.5.5 0 0 0 .243.429l1.098.66a4.25 4.25 0 1 1 8.318 0l1.098-.66a.5.5 0 0 0 .243-.429V5.208a.5.5 0 0 0-.243-.429zM8 8.25a2.75 2.75 0 1 0 0 5.5 2.75 2.75 0 0 0 0-5.5"
		fill={inverse ? token('color.icon.inverse') : token('color.icon')}
		transform={`matrix(0.9,0,0,0.9,${anchor.x + 1.5},${anchor.y + 2.5})`}
	/>
);

interface IconSetProps {
	setRef?: (node: SVGGElement) => void;
	anchor: Point;
	icons: TransitionIcons[];
	inverse?: boolean;
}

export const IconSet = ({
	setRef,
	anchor,
	icons,
	inverse = false,
}: IconSetProps): React.JSX.Element => (
	<g ref={setRef}>
		{icons.map((iconType, index) => {
			switch (iconType) {
				case TransitionIcons.TriggerAgent:
					return (
						<TriggerAgentIcon
							key="agent"
							inverse={inverse}
							anchor={add(anchor, toPoint(index * ICON_PADDING, 0))}
						/>
					);
				case TransitionIcons.Bolt:
					return (
						<BoltIcon
							key="bolt"
							inverse={inverse}
							anchor={add(anchor, toPoint(index * ICON_PADDING, 0))}
						/>
					);
				case TransitionIcons.Error:
					return (
						<ErrorIcon
							key="error"
							inverse={inverse}
							anchor={add(anchor, toPoint(index * ICON_PADDING, 0))}
						/>
					);
				case TransitionIcons.Check:
					return <CheckIcon key="check" anchor={add(anchor, toPoint(index * ICON_PADDING, 0))} />;
				case TransitionIcons.CheckInactive:
					return (
						<CheckInactiveIcon
							key="check-inactive"
							anchor={add(anchor, toPoint(index * ICON_PADDING, 0))}
						/>
					);
				case TransitionIcons.Cross:
					return <CrossIcon key="cross" anchor={add(anchor, toPoint(index * ICON_PADDING, 0))} />;
				case TransitionIcons.CrossInactive:
					return (
						<CrossInactiveIcon
							key="cross-inactive"
							anchor={add(anchor, toPoint(index * ICON_PADDING, 0))}
						/>
					);
				case TransitionIcons.SharedTransition:
					return (
						<SharedTransitionIcon
							key={iconType}
							anchor={add(anchor, toPoint(index * ICON_PADDING, 0))}
						/>
					);
				default:
					return null;
			}
		})}
	</g>
);
